import { useMemo } from 'react';
import { useAlert } from '@uplift/ui-v2';

import { Box, Link, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { CareStatus, resendStudentInvite, StudentInviteStatus } from '@uplift/api';

import { StudentsTableColumnRenderArgs } from '../types';

export type StatusCellProps = StudentsTableColumnRenderArgs<'status'> & { isCareApp: boolean };

export const StatusCell: React.VFC<StatusCellProps> = ({ row, value, isCareApp }) => {
  const { classes } = useStatusCellStyles();
  const alert = useAlert();
  const isInvited = value === StudentInviteStatus.invited && row?.student?.lastLogin === null;
  const isEstablishedCare = row?.careStatus === CareStatus.established;
  const isPatientAttendedAppointment = row?.patientAttendedAppointment === true;
  const isDeactivated = row?.userActive === false;
  const isAccountCreated = row?.status === StudentInviteStatus.accepted && !isPatientAttendedAppointment;

  const handleResendInvite = async () => {
    await resendStudentInvite(row.student.id);
    alert.success(`Referral has been sent to ${row.student.fullName} (${row.student.email}).`);
  };

  const getStudentStatus = useMemo(() => {
    if (isDeactivated) return 'Deactivated';
    if (isAccountCreated) return 'Account Created';
    if (isEstablishedCare || isPatientAttendedAppointment) return 'Established Care';
  }, [ isAccountCreated, isDeactivated, isEstablishedCare, isPatientAttendedAppointment ]);

  return (
    <Box width="100%">
      {isInvited && !isDeactivated && (
        <>
          <Typography variant="body2" className={classes.status} data-testid="students-table-status">
            Invited
          </Typography>
          {isCareApp && (
            <Link
              variant="body2"
              className={classes.resendInvite}
              onClick={handleResendInvite}
              data-testid="students-table-resend-invite"
            >
              Resend Invite
            </Link>
          )}
        </>
      )}
      <Typography variant="body2" className={classes.status} data-testid="students-table-status">
        {getStudentStatus}
      </Typography>
    </Box>
  );
};

const useStatusCellStyles = makeStyles({ name: 'StatusCell' })((theme) => ({
  status: {
    color: theme.palette.text.primary,
    letterSpacing: '0.4px',
    fontWeight: 400,
  },
  resendInvite: {
    color: theme.palette.text.disabled,
    '&:hover': {
      color: theme.palette.text.primary,
    },
    textDecoration: 'underline',
    cursor: 'pointer',
    letterSpacing: '0.4px',
    fontWeight: 400,
  },
}));
